import { useState, useEffect } from "react";
import API from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Trophy, TrendUp, TrendDown, Timer, ShieldCheck, Warning, Truck } from "@phosphor-icons/react";

const GRADE_STYLES = {
  A: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 border-emerald-300",
  B: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400 border-blue-300",
  C: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 border-amber-300",
  D: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 border-red-300",
};

export default function VendorScorecardPage() {
  const [scorecards, setScorecards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("score");

  useEffect(() => {
    API.get("/analytics/vendor-scorecard").then(({ data }) => setScorecards(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const sorted = [...scorecards].sort((a, b) => {
    if (sortBy === "score") return b.score - a.score;
    if (sortBy === "tasks") return b.total_tasks - a.total_tasks;
    if (sortBy === "speed") return a.avg_turnaround_days - b.avg_turnaround_days;
    if (sortBy === "compliance") return b.verified_docs - a.verified_docs;
    return 0;
  });

  return (
    <div data-testid="vendor-scorecard-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight">Supplier Scorecard</h1>
          <p className="text-sm text-muted-foreground mt-1">Automated supplier ratings based on performance, compliance, and speed</p>
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[150px]" data-testid="scorecard-sort">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="score">Score</SelectItem>
            <SelectItem value="tasks">Total Tasks</SelectItem>
            <SelectItem value="speed">Turnaround</SelectItem>
            <SelectItem value="compliance">Compliance</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-md">
          <Truck size={32} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-muted-foreground text-sm">No suppliers to score</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {sorted.map((v, idx) => (
            <div key={v.vendor_id} className="border border-border rounded-md p-4 bg-card hover:shadow-sm transition-shadow" data-testid={`scorecard-${v.vendor_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {idx < 3 && <Trophy size={16} className={idx === 0 ? "text-amber-500" : idx === 1 ? "text-gray-400" : "text-amber-700"} weight="fill" />}
                    <p className="text-sm font-semibold truncate">{v.company_name}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">{v.trade}</p>
                </div>
                <div className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold border ${GRADE_STYLES[v.grade]}`}>
                  {v.grade}
                </div>
              </div>

              <div className="mb-3">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Score</span>
                  <span className="font-bold">{v.score}/100</span>
                </div>
                <Progress value={v.score} className="h-2" />
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-1.5 p-2 rounded bg-muted/50">
                  <TrendUp size={14} className="text-emerald-500" />
                  <div>
                    <p className="text-muted-foreground">Completed</p>
                    <p className="font-semibold">{v.completed_tasks}/{v.total_tasks}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 p-2 rounded bg-muted/50">
                  <Timer size={14} className="text-blue-500" />
                  <div>
                    <p className="text-muted-foreground">Avg Time</p>
                    <p className="font-semibold">{v.avg_turnaround_days}d</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 p-2 rounded bg-muted/50">
                  <ShieldCheck size={14} className="text-purple-500" />
                  <div>
                    <p className="text-muted-foreground">Compliance</p>
                    <p className="font-semibold">{v.verified_docs}/{v.compliance_docs}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 p-2 rounded bg-muted/50">
                  {v.overdue_tasks > 0 ? <Warning size={14} className="text-red-500" /> : <TrendDown size={14} className="text-emerald-500" />}
                  <div>
                    <p className="text-muted-foreground">Overdue</p>
                    <p className={`font-semibold ${v.overdue_tasks > 0 ? "text-red-600" : ""}`}>{v.overdue_tasks}</p>
                  </div>
                </div>
              </div>

              {v.expired_docs > 0 && (
                <div className="mt-2 px-2 py-1 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-[10px] font-medium">
                  {v.expired_docs} expired compliance doc(s)
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
